import { useEffect } from 'react'

// Fallback line height (px) for mouse wheels that report `deltaY` in lines.
const LINE_HEIGHT = 16

/** Wheel `deltaY` in CSS pixels regardless of the device's `deltaMode`
 *  (pixel / line / page). A real mouse wheel often reports lines — deltaY ≈ 3 —
 *  which, taken as pixels, scrolls so little the panel looks frozen. */
export function wheelDeltaToPixels(
  event: WheelEvent,
  node: HTMLElement,
): number {
  if (event.deltaMode === WheelEvent.DOM_DELTA_LINE)
    return event.deltaY * LINE_HEIGHT
  if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE)
    return event.deltaY * node.clientHeight
  return event.deltaY
}

/**
 * Lets a scroll container respond to the wheel even when an ancestor has locked
 * native scrolling — notably a Radix `Dialog`, whose `react-remove-scroll`
 * `preventDefault`s wheel events on any content portaled outside the dialog
 * (selects, popovers, custom panels). We attach a non-passive `wheel` listener
 * (React's `onWheel` is passive, so it cannot `preventDefault`) and translate
 * `deltaY` into `scrollTop` ourselves. No-op while the content fits, so page
 * scroll behind a non-scrollable panel is left untouched.
 */
export function useWheelScrollProxy(
  ref: React.RefObject<HTMLElement | null>,
): void {
  useEffect(() => {
    const node = ref.current
    if (!node) return
    const onWheel = (event: WheelEvent) => {
      if (node.scrollHeight <= node.clientHeight) return
      node.scrollTop += wheelDeltaToPixels(event, node)
      event.preventDefault()
      event.stopPropagation()
    }
    node.addEventListener('wheel', onWheel, { passive: false })
    return () => node.removeEventListener('wheel', onWheel)
  }, [ref])
}
